import React, { FunctionComponent, ReactNode, useState } from 'react'

import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import { ActionMeta, GroupBase } from 'react-select'
import { CreatableProps } from 'react-select/creatable'
import { OnChangeValue } from 'react-select/dist/declarations/src/types'
import { SpaceProps, WidthProps } from 'styled-system'

import { CreatableInput } from '@/atoms/input'
import { SelectOption } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: string
  fieldProps?: SpaceProps & WidthProps
  placeholder?: ReactNode
  name?: string
  hideErrorMessage?: boolean
  withTranslation?: boolean
  tooltip?: string
}

export type MultiCreatableFieldProps = OwnProps & CreatableProps<SelectOption, true, GroupBase<SelectOption>>

const MultiCreatableField: FunctionComponent<MultiCreatableFieldProps> = ({
  hideErrorMessage,
  label,
  fieldProps,
  placeholder,
  name,
  withTranslation = false,
  tooltip,
  ...rest
}) => {
  const { t } = useTranslation()
  const [field, meta, helpers] = useField(name)
  const [inputValue, setInputValue] = useState('')
  const hasError: boolean = meta.touched && (!!meta.error || !!meta.error?.length)

  const values = field.value?.map((value: string) => ({ value, label: withTranslation ? t(`field.${value}_option`) : value })) ?? []

  const handleOnChange = (value: OnChangeValue<SelectOption, true>, actionMeta?: ActionMeta<SelectOption>): void => {
    if (actionMeta && actionMeta.action === 'clear') {
      helpers.setValue([])
      return
    }

    const valuesList = value?.map(option => option.value) || []
    helpers.setValue(valuesList)
  }

  const handleInputChange = (inputValue: string) => setInputValue(inputValue)

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
      <CreatableInput
        hasError={hasError}
        value={values}
        inputValue={inputValue}
        name={field.name}
        onChange={handleOnChange}
        placeholder={placeholder}
        onInputChange={handleInputChange}
        isMulti
        {...rest}
      />
    </FieldWrapper>
  )
}

MultiCreatableField.displayName = 'MultiCreatableField'

export default MultiCreatableField
